import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import React, { type FC } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import type { SearchFieldVariant } from "~/feature/search/type";

import * as Styled from "./SearchInput.styles";

interface ISearchInputProps {
  params: AutocompleteRenderInputParams;
  loading: boolean;
  placeholder: string;
  onClear?: () => void;
  autoFocus?: boolean;
  variant?: SearchFieldVariant;
}

type RefLike<T> =
  | ((instance: T | null) => void)
  | { current: T | null }
  | null
  | undefined;

const assignRef = <T,>(ref: RefLike<T>, value: T | null): void => {
  if (!ref) return;
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  ref.current = value;
};

export const SearchInput: FC<ISearchInputProps> = ({
  params,
  loading,
  placeholder,
  onClear,
  autoFocus,
  variant = "header",
}) => {
  // v9: AutocompleteRenderInputParams exposes slotProps.input (container) and
  // slotProps.htmlInput (native input) instead of InputProps/inputProps.
  const { input, htmlInput } = params.slotProps;
  const inputValue = htmlInput.value as string | undefined;
  const hasValue = Boolean(inputValue && inputValue.trim().length > 0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const setRootRef = useMemo(() => {
    return (node: HTMLDivElement | null) => {
      assignRef(input.ref as RefLike<HTMLDivElement>, node);
    };
  }, [input.ref]);

  const setInputElRef = useMemo(() => {
    return (node: HTMLInputElement | null) => {
      inputRef.current = node;
      assignRef(htmlInput.ref as RefLike<HTMLInputElement>, node);
    };
  }, [htmlInput.ref]);

  useEffect(() => {
    if (!autoFocus) return;
    requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
  }, [autoFocus]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      input.onMouseDown?.(e);
    },
    [input]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onClear?.();
    },
    [onClear]
  );

  return (
    <Styled.Search
      ref={setRootRef}
      onMouseDown={handleMouseDown}
      variant={variant}
    >
      <Styled.SearchIconWrapper variant={variant}>
        <SearchIcon />
      </Styled.SearchIconWrapper>

      <Styled.StyledInputBase
        variant={variant}
        autoFocus={autoFocus}
        placeholder={placeholder}
        inputRef={setInputElRef}
        inputProps={{
          ...htmlInput,
          "aria-label": "search",
        }}
      />

      <Styled.EndAdornmentWrapper variant={variant}>
        {loading ? (
          <CircularProgress size={18} />
        ) : (
          hasValue && (
            <IconButton
              size="small"
              onClick={handleClear}
              aria-label="clear search"
              sx={{ padding: 0.5 }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )
        )}
      </Styled.EndAdornmentWrapper>
    </Styled.Search>
  );
};
